import { get } from 'lodash';

import axios from 'axios';
import BackendConnector from './backendConnector';

export default class PositionHttpService {
    currentGeneratorInitRequest = null;

    static getPosition(positionId, queryString = '') {
        return BackendConnector.get(`position/${positionId}${queryString}`);
    }

    static getPositions(data, requestConfig) {
        return BackendConnector.post('position/search', data, requestConfig);
    }

    static getSyncedPositions(employerId, atsJobId) {
        return BackendConnector.get(`positions/employer/${employerId}/syncToAts/${atsJobId}`);
    }

    static updatePositionSearchCriteria(positionId, { talentId, desiredCandidateProfile, stage }) {
        return BackendConnector.post(`position/${positionId}/updateSearchCriteria`, {
            talentId,
            desiredCandidateProfile,
            stage,
        });
    }

    static updatePositionDiversityOptimizations(positionId, { optimizations, diversityIds, employerId }) {
        return BackendConnector.put(`position/${positionId}/diversity/updateOptimizations`, {
            optimizations,
            diversityIds,
            employerId,
        });
    }

    static updatePositionDiversityOptimizationsStatus(positionId, { id, status }) {
        return BackendConnector.put(`position/${positionId}/diversity/updateStatus`, {
            id,
            status,
        });
    }

    static updatePositionAutoCampaign(positionId, data) {
        return BackendConnector.put(`position/${positionId}/switchAutoCampaign`, data);
    }

    static clonePosition(position) {
        return BackendConnector.post('position/clone', position);
    }

    static cloneCandidateToOtherPosition(currentPositionId, candidateId, otherPositionId) {
        return BackendConnector.post(`position/${currentPositionId}/cloneCandidate/${candidateId}/toOtherPosition/${otherPositionId}`);
    }

    static regeneratePositionCriteria(positionName, positionId) {
        if (positionId) {
            return BackendConnector.post(`position/${positionId}/regeneratePositionCriteria`, { positionName });
        }
        return BackendConnector.post('position/regeneratePositionCriteria', { positionName });
    }

    static updatePosition(positionId, position, stage = 'manage') {
        return BackendConnector.put(`position/${positionId}?stage=${stage}`, position);
    }

    static setFeedbackRecipients(positionId, feedbackRecipients) {
        return BackendConnector.post(`position/${positionId}/setFeedbackRecipients`, feedbackRecipients);
    }

    static changePositionStatus(positionId, statusData) {
        return BackendConnector.put(`position/${positionId}/status`, statusData);
    }

    static cancellableInitAutoFeedbackGenerator({ positionId, isNeedToRespectLock = false, action = null }) {
        const REQUEST_CANCEL_MSG = 'CANCEL';

        if (this.currentGeneratorInitRequest && this.currentGeneratorInitRequest.cancel) {
            this.currentGeneratorInitRequest.cancel(REQUEST_CANCEL_MSG);
        }

        this.currentGeneratorInitRequest = axios.CancelToken.source();

        return BackendConnector.post(
            `position/${positionId}/wizard/generateFeedback/0/`,
            { isNeedToRespectLock, useOnlineUpdate: true, action },
            { cancelToken: this.currentGeneratorInitRequest.token },
        ).finally(() => {
            this.currentGeneratorInitRequest = null;
        });
    }

    static initAutoFeedbackGenerator({ positionId, isNeedToRespectLock = false, action = null }) {
        return BackendConnector.post(`position/${positionId}/wizard/generateFeedback/0/`, { isNeedToRespectLock, useOnlineUpdate: true, action });
    }

    static async candidatesSendBackToReview({ positionId, talentIds, isBulkAction = false }) {
        return BackendConnector.post(`position/${positionId}/sendCandidatesBackToReview`, { talentIds, isBulkAction });
    }

    static async candidatesSendBackToQualified({ positionId, talentIds }) {
        return BackendConnector.post(`position/${positionId}/sendCandidatesBackToQualified`, { talentIds });
    }

    static async getProgressAIScore(positionId) {
        return BackendConnector.get(`position/${positionId}/getProgressAIScore`);
    }

    static async updateProgressAIScore(positionId, progressAIScore) {
        return BackendConnector.post(`position/${positionId}/updateProgressAIScore`, { progressAIScore });
    }

    static async getPositionGeneratedCandidatesMatchScore(positionId) {
        return BackendConnector.get(`position/${positionId}/matchScore`);
    }

    static async applyAutoFeedbackToCandidates(positionId, talentIds, action = null) {
        return BackendConnector.post(`position/${positionId}/applyAutoFeedback`, { talentIDs: talentIds, action });
    }

    static getDiversityOptimizations(positionId) {
        return BackendConnector.get(`position/${positionId}/diversity/getOptimizations`);
    }

    static getDiversityRecommendations({
        positionId, diversityIds, filter, skipRecommendationsForNotInitialized,
    }) {
        if (positionId) {
            return BackendConnector.post(`position/${positionId}/diversity/getRecommendations`, { diversityIds, skipRecommendationsForNotInitialized });
        }
        return BackendConnector.post('position/diversity/getRecommendations', { diversityIds, filter });
    }

    static getDiversityStats({
        positionId, filter, diversityIds, isChangeRequest = false,
    }) {
        if (filter) {
            return BackendConnector.post('position/diversity/getStats', {
                filter, diversityIds, isChangeRequest, positionId,
            });
        }
        return BackendConnector.post(`position/${positionId}/diversity/getStats`, { diversityIds, isChangeRequest });
    }

    static getAllDiversityBreakdowns(positionId) {
        return BackendConnector.get(`position/${positionId}/getAllDiversityBreakdowns`);
    }

    static getLastDiversityTypeApplied(positionId) {
        return BackendConnector.get(`position/${positionId}/diversity/getLastDiversityTypeApplied`);
    }

    static initDiversityOptimizer(positionId) {
        return BackendConnector.post(`position/${positionId}/diversity/initOptimizer`);
    }

    static shareDiversityOptimizer(positionId, data) {
        return BackendConnector.post(`position/${positionId}/diversity/shareOptimizer`, data);
    }

    static revertToOriginalSearch(positionId) {
        return BackendConnector.post(`position/${positionId}/diversity/revertToOriginal`);
    }

    static getCandidateWorkflowStep({ positionId, talentId, type = 'employer' }) {
        return BackendConnector.get(`position/${positionId}/talentId/${talentId}/workflowStep/${type}`);
    }

    static getCandidatesWorkflowSteps({ positionId, talentIds, type = 'employer' }) {
        return BackendConnector.post(`position/${positionId}/talents/workflowStep/${type}`, { talentIds });
    }

    static checkTalentsContactChannelsAvailability({ positionId, talentIds }) {
        return BackendConnector.post(`/position/${positionId}/checkTalentsContactChannelsAvailability`, { talentIds });
    }

    static syncAtsCandidates({ positionId }) {
        return BackendConnector.post(`/position/${positionId}/syncAtsCandidates`);
    }

    static checkIfCandidateCampaignsExist({
        positionId, talentIds, active, autoContactIssues,
    }) {
        return BackendConnector.post(`/position/${positionId}/checkIfCandidateCampaignsExist`, { talentIds, active, autoContactIssues });
    }

    static async checkIfAtsJobExists({
        employerId, atsJobId,
    }) {
        const response = await BackendConnector.get(`/ats/employer/${employerId}/atsJobs/${atsJobId}/exists`);
        const taskId = get(response, 'taskId');

        if (!taskId) {
            return undefined;
        }

        const result = await BackendConnector.pollUntilTaskFinished(taskId);
        return result;
    }

    static async atsJobImport({ employerId, atsJobId }) {
        const response = await BackendConnector.post('/ats/job/import', { employerId, atsJobId });
        const taskId = get(response, 'taskId');

        if (!taskId) {
            return undefined;
        }

        const result = await BackendConnector.pollUntilTaskFinished(taskId);
        return result;
    }

    static sendFeedbackRequestToEmployerRep(positionId) {
        return BackendConnector.post(`/position/${positionId}/sendFeedbackRequestToEmployerRep`);
    }

    static isPositionRequiresCalibration(positionId) {
        return BackendConnector.get(`/position/${positionId}/isPositionRequiresCalibration`);
    }

    static getAccountManagerEmail(positionId) {
        return BackendConnector.post(`position/${positionId}/accountManager/email`);
    }

    static getApplicants(positionId) {
        return BackendConnector.post(`position/${positionId}/applicantsQty`);
    }

    static getAIJobDescription(positionData) {
        return BackendConnector.post('position/getAIJobDescription', { ...positionData });
    }

    static getAssociatedEmployerRepAsSenderUser(positionId) {
        return BackendConnector.post(`position/${positionId}/associatedEmployerRep/senderUser`);
    }

    static async handlePositionRecalibration(positionId, type, recalibrateSinglePosition = false) {
        const response = await BackendConnector.post(`position/${positionId}/recalibration/${type}`, { recalibrateSinglePosition });

        const taskId = response?.taskId;

        if (taskId) {
            return BackendConnector.pollUntilTaskFinished(taskId);
        }

        return undefined;
    }

    static switchSourcingTypeForPosition({ positionId, autoCampaignData }) {
        return BackendConnector.post(`/position/${positionId}/switchSourcingType`, { autoCampaignData });
    }

    static processPositionForManualMessaging(positionId) {
        return BackendConnector.post(`/position/${positionId}/manualMessaging`);
    }

    static getAtsSubmittedCandidatesCountForManualMessaging(positionId) {
        return BackendConnector.get(`/position/${positionId}/manualMessaging/getAtsSubmittedCandidatesCount`);
    }

    static processCandidateForManualMessaging({ positionId, feedback }) {
        return BackendConnector.post(`/position/${positionId}/manualMessaging/processCandidate`, { feedback });
    }
}
